/**
 * A module's playlist, as the row of tiles a card and a screen both show.
 *
 * This is the piece that makes a module look like what it is: a horizontal strip
 * of tall capsule cards, the shape the app already gives a row of suggested videos
 * (`#/components/interstitials/TrendingVideos`) and a learner's shelf of books
 * (`#/components/flashbooks/FlashBookShelf`). It is written once here for the same
 * reason those are written once: two hand-rolled rows drift, and a module's card
 * and a module's own screen must not be two answers to one question.
 *
 * ## Why it is built the way it is
 *
 * A carousel's whole job is to *look like something you can scroll*, and the first
 * build of this one did not: the tiles were small, nothing said how far the strip
 * went, and a reader had to guess whether the row was a picture or a list. So four
 * things are deliberate here:
 *
 *  - **A slice, not a screenful.** Tiles are a fixed width and the strip is inset
 *    so the next one is always partly under the edge. On a phone two full tiles and
 *    a third's worth are visible, which is the oldest affordance there is and the
 *    only one that needs no explanation.
 *  - **A fade on the edge that has more, and none on the edge that does not.** It
 *    is drawn from the scroll position rather than guessed, so a reader who has
 *    reached the end sees the row finish cleanly instead of being told there is
 *    more behind a wall — and it arrives and leaves on a fade of its own, because a
 *    gradient that pops into existence at the edge of a row being dragged reads as
 *    a glitch rather than as an affordance (see `FADE_MS`).
 *  - **A snap.** The strip stops on a tile rather than between two, because the
 *    order is the promise of a playlist and half a card at the edge reads as a
 *    broken one.
 *  - **A rail under it** — one segment per capsule, filled up to the one on
 *    screen. With six capsules it is a progress line; with thirty it is still one
 *    line rather than thirty dots, which is what a dot row becomes. It reads as
 *    *where the strip is*, not *how far the reader has got*, which is why the card
 *    in a feed leaves it out — see `showRail`.
 *
 * One thing it must *not* do is hide its own overflow. `overflow-x: hidden` is a
 * scroller a finger cannot drag and a wheel cannot turn — it still answers to
 * `scrollLeft`, which is why the mistake is invisible to a script and obvious to a
 * person (see the note in the styles below).
 *
 * Everything it needs is handed in — the tiles, already resolved (see
 * `#/lib/moduleTiles`), and what pressing one does. What it owns is the *row*.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {
  Animated,
  Easing,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native'
import {LinearGradient} from 'expo-linear-gradient'

import {type ModuleTile} from '#/lib/moduleTiles'
import {atoms as a, useTheme} from '#/alf'
import {ModuleCapsuleTile} from './ModuleCapsuleTile'

/**
 * How wide one tile is.
 *
 * Wider than the first build's 132: at 152 a 390pt phone shows two full tiles and
 * most of a third, and a tile is comfortably past the 44pt every touch target
 * needs. A desktop window shows four or five rather than seven, which keeps a
 * playlist a row of objects instead of a wall of cards.
 */
export const MODULE_TILE_WIDTH = 152

/** How far apart tiles sit. */
const GAP = 12

/** How wide the fade at a live edge is. */
const FADE = 40

/** How far a scroll must travel before the rail moves a step. */
const STEP = MODULE_TILE_WIDTH + GAP

/**
 * How long a fade takes to arrive or to leave, in ms.
 *
 * Long enough to be seen as a fade (a timing of 0 is the blink this exists to remove),
 * short enough that the fade is at full strength before a finger has dragged the strip
 * a tile further.
 */
const FADE_MS = 200

export function ModulePlaylistCarousel({
  tiles,
  onPressTile,
  testID,
  tileTestIDPrefix,
  renderTileExtra,
  maxWidth,
  fadeColor,
  showRail = true,
  accessibilityHint,
}: {
  tiles: readonly ModuleTile[]
  /** What pressing a tile does. Absent for a row that is only being looked at. */
  onPressTile?: (tile: ModuleTile) => void
  testID?: string
  /** A name per tile, so a recipe can drive one: `<prefix>-<position>`. */
  tileTestIDPrefix?: string
  /** The studio's furniture: a menu per tile, drawn inside it. */
  renderTileExtra?: (tile: ModuleTile) => React.ReactNode
  /** Where the strip stops being allowed to start scrolling from. */
  maxWidth?: number
  /**
   * The colour the fades dissolve into.
   *
   * It has to be the colour of the surface the strip is *sitting on*, not the
   * screen's: a card is a slightly lighter panel than the page behind it (see the
   * note in `ModuleFeedEmbed`), and a fade drawn in the page's colour paints a dark
   * band across the card's edge — which is exactly what the first build of this row
   * did, because it could only guess. Defaults to the screen's background, which is
   * right on a page and wrong inside anything coloured.
   */
  fadeColor?: string
  /**
   * Whether to draw the rail under the strip. Off for the card in a feed.
   *
   * The rail is a *scroll* affordance — one segment per capsule, filled up to the
   * tile on screen — but a line of segments under a row of capsules, on a card, reads
   * as *progress*: the same fraction the card says in words one line lower (*You have
   * finished 1 of 6 capsules*). Two readings of the same row is one too many, and the
   * one that is a lie is the bar, because scrolling a preview is not progress through
   * a module. On the module's own page the strip is the subject and the rail is doing
   * its real job, so it stays there — and the tiles there carry the played marks,
   * which the card's tiles do not.
   */
  showRail?: boolean
  accessibilityHint?: string
}) {
  const t = useTheme()
  const fade = fadeColor ?? t.atoms.bg.backgroundColor
  const [index, setIndex] = useState(0)
  const [more, setMore] = useState({left: false, right: tiles.length > 2})
  const layout = useRef({width: 0, content: 0})

  /*
   * The fades' own opacity, on a fade.
   *
   * The decision to draw an edge is a threshold (`more`, four pixels of scroll), and
   * drawing it by the threshold makes the gradient *appear* the instant the strip
   * passes it — a band of colour popping in at the edge of the row the reader is
   * dragging. It arrives over `FADE_MS` instead, and leaves the same way, so a strip
   * scrolled a little and back does not blink at either end. It is also what the
   * first paint does: the right-hand fade is there from the start (there are more
   * tiles than fit), and it fades in rather than being simply present.
   *
   * `Animated` rather than `react-native-reanimated`, which is what the rest of the
   * app reaches for: reanimated's timing does not tick in this app's web build — a
   * fade driven by `withTiming` sat at its starting value and never moved, measured,
   * while the same fade as an `Animated.View` opacity animates. `useNativeDriver` is
   * false for the same reason it is false in `NotificationFeedItem` and
   * `CompletionScreen`: opacity is not a native-drivable style on web, and this row is
   * a list of twelve pixels of gradient, not something that needs a native thread.
   */
  const leftFade = useRef(new Animated.Value(0)).current
  const rightFade = useRef(new Animated.Value(0)).current
  useEffect(() => {
    const timing = {
      duration: FADE_MS,
      easing: Easing.out(Easing.quad),
      useNativeDriver: false,
    } as const
    Animated.timing(leftFade, {...timing, toValue: more.left ? 1 : 0}).start()
    Animated.timing(rightFade, {...timing, toValue: more.right ? 1 : 0}).start()
  }, [more.left, more.right, leftFade, rightFade])

  /*
   * Where the strip is, in one place.
   *
   * The rail and the two fades all come from this one handler rather than from a
   * library: three readers of the same number would otherwise be three chances to
   * disagree about which tile the reader is looking at.
   */
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const {contentOffset, contentSize, layoutMeasurement} = event.nativeEvent
      const x = Math.max(0, contentOffset.x)
      const maxX = Math.max(0, contentSize.width - layoutMeasurement.width)
      setIndex(Math.round(x / STEP))
      setMore({left: x > 4, right: x < maxX - 4})
    },
    [],
  )

  const onLayout = useCallback(
    (event: {nativeEvent: {layout: {width: number}}}) => {
      const width = event.nativeEvent.layout.width
      const wasEmpty = layout.current.width === 0
      layout.current.width = width
      if (wasEmpty) {
        setMore(current => ({
          left: false,
          right: layout.current.content > width + 4 || current.right,
        }))
      }
    },
    [],
  )

  const onContentSizeChange = useCallback((content: number) => {
    layout.current.content = content
    setMore(current => ({
      left: current.left,
      right: content > layout.current.width + 4,
    }))
  }, [])

  return (
    <View style={[a.gap_xs, {maxWidth}]}>
      <View style={[a.relative]}>
        <ScrollView
          testID={testID}
          horizontal
          showsHorizontalScrollIndicator={false}
          decelerationRate="fast"
          snapToInterval={STEP}
          snapToAlignment="start"
          onScroll={onScroll}
          scrollEventThrottle={16}
          onLayout={onLayout}
          onContentSizeChange={onContentSizeChange}
          accessibilityHint={accessibilityHint}
          contentContainerStyle={styles.row}>
          {tiles.map(tile => (
            <View key={tile.key} style={[a.relative]}>
              <ModuleCapsuleTile
                tile={tile}
                width={MODULE_TILE_WIDTH}
                onPress={onPressTile ? () => onPressTile(tile) : undefined}
                testID={
                  tileTestIDPrefix
                    ? `${tileTestIDPrefix}-${tile.position}`
                    : undefined
                }>
                {renderTileExtra?.(tile)}
              </ModuleCapsuleTile>
            </View>
          ))}
        </ScrollView>

        {/*
          The fades.

          They are drawn *over* the strip and never touch it: `pointerEvents`
          none, so a press on the tile under a fade is still a press on the tile.
          The colour is the screen's own background, so the same component fades
          correctly in a feed, on a page, and inside a coloured panel.

          Their opacity is animated, on both edges (see `leftFade` above): the
          gradient grows in and out over `FADE_MS` rather than switching on at the
          threshold, so an edge that gains or loses tiles mid-drag does not blink.
        */}
        <Animated.View
          testID={testID ? `${testID}-fade-left` : undefined}
          pointerEvents="none"
          style={[styles.fade, styles.fadeLeft, {opacity: leftFade}]}>
          <LinearGradient
            colors={[fade, 'transparent']}
            start={{x: 0, y: 0.5}}
            end={{x: 1, y: 0.5}}
            style={StyleSheet.absoluteFill}
          />
        </Animated.View>
        <Animated.View
          testID={testID ? `${testID}-fade-right` : undefined}
          pointerEvents="none"
          style={[styles.fade, styles.fadeRight, {opacity: rightFade}]}>
          <LinearGradient
            colors={['transparent', fade]}
            start={{x: 0, y: 0.5}}
            end={{x: 1, y: 0.5}}
            style={StyleSheet.absoluteFill}
          />
        </Animated.View>
      </View>

      {/*
        The rail: one segment per capsule, the one on screen filled.

        Only drawn for a playlist long enough to be scrolled — a rail under two
        tiles is decoration, and the tiles already say how many there are — and only
        where the row is the subject (see `showRail`).
      */}
      {showRail && tiles.length > 2 ? (
        <View
          testID={testID ? `${testID}-rail` : undefined}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          style={[a.flex_row, a.gap_xs, a.justify_center, a.pt_xs]}>
          {tiles.map((tile, tileIndex) => (
            <View
              key={tile.key}
              style={[
                styles.segment,
                {
                  backgroundColor:
                    tileIndex <= index
                      ? t.palette.primary_500
                      : t.atoms.bg_contrast_100.backgroundColor,
                },
              ]}
            />
          ))}
        </View>
      ) : null}
    </View>
  )
}

/*
 * The row is a scroller, and only the platform gets to decide how it scrolls.
 *
 * No `overflow` is set on the strip: `react-native-web` already gives a horizontal
 * `ScrollView` the right one (`overflow-x: auto`), and an `overflow_hidden` here —
 * which looks harmless, and is what a fade *seems* to want — silently takes the
 * strip's scroll away from every finger and wheel. The fades need no help: they sit
 * over the row and are `pointerEvents="none"`.
 */
const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: GAP,
    paddingVertical: 2,
  },
  fade: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: FADE,
  },
  fadeLeft: {
    left: 0,
  },
  fadeRight: {
    right: 0,
  },
  segment: {
    flex: 1,
    maxWidth: 48,
    height: 3,
    borderRadius: 2,
  },
})
